import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { 
  Users, 
  GraduationCap, 
  FileText, 
  Award, 
  TrendingUp, 
  Download,
  Building2,
  UserCheck,
  UserX,
  Clock,
  BarChart3,
  RefreshCw,
  Calendar
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Date range presets
const DATE_PRESETS = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "All time", days: null },
  { label: "Custom", days: "custom" }
];

function AnalyticsDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [classesAnalytics, setClassesAnalytics] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [exporting, setExporting] = useState(false);
  const [datePreset, setDatePreset] = useState("Last 30 days");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showCustomDates, setShowCustomDates] = useState(false);

  const isAdmin = user?.role === "admin";

  // Calculate date range based on preset
  const getDateRange = useCallback(() => {
    const preset = DATE_PRESETS.find(p => p.label === datePreset);
    if (!preset || preset.days === null) {
      return { start: "", end: "" };
    }
    if (preset.days === "custom") {
      return { start: startDate, end: endDate };
    }
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - preset.days);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  }, [datePreset, startDate, endDate]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Build query params with date range
      const { start, end } = getDateRange();
      const params = new URLSearchParams();
      if (selectedClassId) params.append("class_id", selectedClassId);
      if (start) params.append("start_date", start);
      if (end) params.append("end_date", end);

      const queryString = params.toString();
      const overviewUrl = `${API}/analytics/overview${queryString ? '?' + queryString : ''}`;
      const overviewRes = await axios.get(overviewUrl, { headers });
      setOverview(overviewRes.data);

      // Fetch classes breakdown (without date filter for class list)
      const classesRes = await axios.get(`${API}/analytics/classes`, { headers });
      setClassesAnalytics(classesRes.data);

    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedClassId, getDateRange]);

  useEffect(() => {
    if (!user || (user.role !== "educator" && user.role !== "admin")) {
      navigate("/dashboard");
      return;
    }
    fetchAnalytics();
  }, [user, navigate, fetchAnalytics]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("token");
      
      // Build query params with date range
      const { start, end } = getDateRange();
      const params = new URLSearchParams();
      if (selectedClassId) params.append("class_id", selectedClassId);
      if (start) params.append("start_date", start);
      if (end) params.append("end_date", end);
      
      const queryString = params.toString();
      const exportUrl = `${API}/analytics/export${queryString ? '?' + queryString : ''}`;
      
      const response = await axios.get(exportUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { headers, rows } = response.data;
      
      // Convert to CSV
      const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      ].join("\n");

      // Download file with date range in filename
      const dateRangeStr = start && end ? `_${start}_to_${end}` : "";
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `analytics${dateRangeStr}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-orange-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-yellow-500 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('common.loading', 'Loading analytics...')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-orange-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3" data-testid="analytics-title">
              <BarChart3 className="w-8 h-8 text-yellow-600" />
              {t('analytics.title', 'Analytics Dashboard')}
            </h1>
            <p className="text-gray-600 mt-1">
              {isAdmin 
                ? t('analytics.adminSubtitle', 'Platform-wide performance metrics')
                : t('analytics.educatorSubtitle', 'Track your students\' progress')}
            </p>
          </div>

          <div className="flex flex-wrap gap-3 mt-4 md:mt-0">
            {/* Date Range Filter */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <select
                value={datePreset}
                onChange={(e) => {
                  setDatePreset(e.target.value);
                  setShowCustomDates(e.target.value === "Custom");
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                data-testid="date-range-filter"
              >
                {DATE_PRESETS.map((preset) => (
                  <option key={preset.label} value={preset.label}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom Date Inputs */}
            {showCustomDates && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                  data-testid="start-date-input"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                  data-testid="end-date-input"
                />
              </div>
            )}

            {/* Class Filter */}
            {classesAnalytics.length > 0 && (
              <select
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                data-testid="class-filter"
              >
                <option value="">{isAdmin ? 'All Classes' : 'My Classes'}</option>
                {classesAnalytics.map((cls) => (
                  <option key={cls.class_id} value={cls.class_id}>
                    {cls.class_name}
                  </option>
                ))}
              </select>
            )}

            {/* Refresh Button */}
            <button
              onClick={fetchAnalytics}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              {t('common.refresh', 'Refresh')}
            </button>

            {/* Export Button */}
            <button
              onClick={handleExportCSV}
              disabled={exporting}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4" />
              {exporting ? t('common.exporting', 'Exporting...') : t('analytics.exportCSV', 'Export CSV')}
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={<Users className="w-6 h-6" />}
            label={t('analytics.totalStudents', 'Total Students')}
            value={overview?.total_students || 0}
            color="blue"
          />
          <StatCard
            icon={<TrendingUp className="w-6 h-6" />}
            label={t('analytics.completionRate', 'Completion Rate')}
            value={`${overview?.overall_completion_rate || 0}%`}
            color="green"
          />
          <StatCard
            icon={<FileText className="w-6 h-6" />}
            label={t('analytics.businessPlans', 'Business Plans')}
            value={`${overview?.business_plan_rate || 0}%`}
            color="purple"
          />
          <StatCard
            icon={<Award className="w-6 h-6" />}
            label={t('analytics.certificates', 'Certificates Earned')}
            value={overview?.certificates_earned || 0}
            color="yellow"
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={<Building2 className="w-6 h-6" />}
            label={t('analytics.totalClasses', 'Total Classes')}
            value={overview?.total_classes || 0}
            color="indigo"
            small
          />
          <StatCard
            icon={<UserCheck className="w-6 h-6" />}
            label={t('analytics.activeStudents', 'Active (7 days)')}
            value={overview?.active_students || 0}
            color="emerald"
            small
          />
          <StatCard
            icon={<UserX className="w-6 h-6" />}
            label={t('analytics.inactiveStudents', 'Inactive')}
            value={overview?.inactive_students || 0}
            color="red"
            small
          />
          <StatCard
            icon={<Clock className="w-6 h-6" />}
            label={t('analytics.avgCompletionTime', 'Avg. Completion')}
            value={overview?.avg_completion_time_days ? `${overview.avg_completion_time_days} days` : 'N/A'}
            color="orange"
            small
          />
        </div>

        {/* Session Completion Chart */}
        {overview?.session_completion?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <GraduationCap className="w-6 h-6 text-yellow-600" />
              {t('analytics.sessionCompletion', 'Session Completion Breakdown')}
            </h2>
            <div className="space-y-4">
              {overview.session_completion.map((session, idx) => (
                <SessionProgressBar
                  key={idx}
                  session={session.session}
                  title={session.title}
                  percentage={session.completion_rate}
                  completed={session.completed_count}
                  total={session.total_students}
                />
              ))}
            </div>
          </div>
        )}

        {/* Classes Breakdown Table */}
        {classesAnalytics.length > 0 && !selectedClassId && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-yellow-600" />
              {t('analytics.classBreakdown', 'Class Breakdown')}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="classes-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">{t('analytics.className', 'Class Name')}</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">{t('analytics.students', 'Students')}</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">{t('analytics.completion', 'Completion')}</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">{t('analytics.businessPlans', 'Plans')}</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">{t('analytics.certificates', 'Certs')}</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">{t('analytics.active', 'Active')}</th>
                  </tr>
                </thead>
                <tbody>
                  {classesAnalytics.map((cls) => (
                    <tr 
                      key={cls.class_id} 
                      className="border-b border-gray-100 hover:bg-yellow-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedClassId(cls.class_id)}
                    >
                      <td className="py-3 px-4 font-medium text-gray-800">{cls.class_name}</td>
                      <td className="py-3 px-4 text-center">{cls.student_count}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                          cls.completion_rate >= 80 ? 'bg-green-100 text-green-700' :
                          cls.completion_rate >= 50 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {cls.completion_rate}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">{cls.business_plans}</td>
                      <td className="py-3 px-4 text-center">{cls.certificates}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-green-600 font-medium">{cls.active_students}</span>
                        <span className="text-gray-400">/{cls.student_count}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {overview?.total_students === 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              {t('analytics.noStudents', 'No students yet')}
            </h3>
            <p className="text-gray-500">
              {t('analytics.noStudentsDesc', 'Students will appear here once they enroll in your classes.')}
            </p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

// Stat Card Component
function StatCard({ icon, label, value, color, small = false }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    red: 'bg-red-100 text-red-600',
    orange: 'bg-orange-100 text-orange-600',
  };

  return (
    <div className={`bg-white rounded-xl shadow-md p-${small ? '4' : '6'} flex items-center gap-4`}>
      <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
        {icon}
      </div>
      <div>
        <p className={`text-${small ? 'xs' : 'sm'} text-gray-500`}>{label}</p>
        <p className={`text-${small ? 'xl' : '2xl'} font-bold text-gray-800`}>{value}</p>
      </div>
    </div>
  );
}

// Session Progress Bar Component
function SessionProgressBar({ session, title, percentage, completed, total }) {
  const getBarColor = (pct) => {
    if (pct >= 80) return 'bg-green-500';
    if (pct >= 50) return 'bg-yellow-500';
    return 'bg-red-400';
  };

  return (
    <div className="flex items-center gap-4">
      <div className="w-12 text-center">
        <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-700 rounded-lg font-bold text-sm">
          {session}
        </span>
      </div>
      <div className="flex-1">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]" title={title}>
            {title}
          </span>
          <span className="text-sm text-gray-500">
            {completed}/{total} ({percentage}%)
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className={`h-3 rounded-full transition-all duration-500 ${getBarColor(percentage)}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export default AnalyticsDashboard;
